function statusFromBoolean(value) {
  return value ? "ready" : "stale";
}

function latestMaintenanceStatus(maintenance) {
  const runs = ((maintenance || {}).runs || []);
  return runs.length ? runs[0].status : "unknown";
}

function destinationId(item) {
  return item?.id || item?.destination_id || "";
}

function destinationDefaultKind(item) {
  if (item?.bound_to_queue_alerts) {
    return "queue_alert";
  }
  if (item?.bound_to_queue_digests) {
    return "queue_digest";
  }
  return "queue_alert";
}

function countMetaRows(rows) {
  return rows.length ? rows.map((item) => `${item.key} (${item.count})`).join(" | ") : "No recent telemetry counts.";
}

export function resolveSupportDestinationFocus(support, selectedDestinationId = null) {
  const items = (support.notification_destinations || []);
  if (!items.length) {
    return null;
  }
  const explicit = items.find((item) => destinationId(item) === selectedDestinationId);
  if (explicit) {
    return explicit;
  }
  return (
    items.find((item) =>
      ["quarantined", "recovery_failed"].includes(String(item.recovery?.recovery_status || "").toLowerCase())
    ) ||
    items.find((item) => String(item.status || "").toLowerCase() !== "active") ||
    items[0]
  );
}

export function buildSupportConsoleSummary(support, options = {}) {
  const workerRuns = (support.worker_health || {}).runs || [];
  const unhealthyRuns = workerRuns.filter((item) => item.backpressure_active || item.failure_streak > 0);
  const quarantined = (support.notification_destinations || []).filter((item) => item.status !== "active");
  const selectedDestination = resolveSupportDestinationFocus(support, options.selectedDestinationId || null);
  const destinationRows = (support.notification_destinations || []).map((item) => {
    const recoveryStatus = String(item.recovery?.recovery_status || "").trim() || "healthy";
    const disabled = String(item.status || "").trim().toLowerCase() === "inactive";
    return {
      id: destinationId(item),
      name: item.name,
      destinationType: item.destination_type,
      status: item.status,
      recoveryStatus,
      recentFailuresTotal: item.recent_failures_total,
      consecutiveLiveFailures: item.consecutive_live_failures,
      quarantineReason: item.quarantine_reason,
      lastDeliveryStatus: item.last_delivery_status,
      lastDeliveryKind: item.last_delivery_kind,
      recommendedAction:
        item.recovery?.recommended_action ||
        (disabled ? "Re-enable this destination before testing delivery." : "Send a test delivery to verify routing."),
      defaultDeliveryKind: destinationDefaultKind(item),
      canRecover:
        item.destination_type === "webhook" &&
        !disabled &&
        ["quarantined", "recovery_failed"].includes(recoveryStatus),
    };
  });
  const actionRows = [];
  const recoveryCandidates = destinationRows.filter((item) => item.canRecover);
  if (recoveryCandidates.length) {
    const target = recoveryCandidates[0];
    actionRows.push({
      kind: "recover_destination",
      label: `Recover ${target.name}`,
      detail: target.recommendedAction,
      destinationId: target.id,
      status: "overdue",
    });
  }
  if ((support.recent_delivery_issues || []).length) {
    actionRows.push({
      kind: "inspect_deliveries",
      label: "Inspect recent delivery failures",
      detail: `${support.recent_delivery_issues.length} recent delivery issue(s) need review.`,
      destinationId: destinationRows[0]?.id || "",
      status: "stale",
    });
  }
  if ((support.recent_incidents || []).length) {
    actionRows.push({
      kind: "review_incidents",
      label: "Review recent incidents",
      detail: `${support.recent_incidents.length} support-visible incident(s) are available in the console.`,
      destinationId: "",
      status: "stale",
    });
  }
  for (const item of support.recommended_actions || []) {
    actionRows.push({
      kind: "recommended",
      label: item,
      detail: `${support.tenant_slug} | ${support.entitlements.plan_label}`,
      destinationId: "",
      status: "ready",
    });
  }
  return {
    metrics: [
      {
        label: "Queue pressure",
        value: support.queue_sla.overdue_total,
        detail: `${support.queue_sla.first_response_overdue_total} first-response overdue`,
      },
      {
        label: "Worker watch",
        value: unhealthyRuns.length,
        detail: `${(support.worker_health || {}).dead_letters?.length || 0} dead letters`,
      },
      {
        label: "Quarantined destinations",
        value: quarantined.length,
        detail: `${support.notification_destinations.length} destinations tracked`,
      },
      {
        label: "Smoke posture",
        value: support.smoke_governance.rotation_status,
        detail: support.smoke_governance.canonical_email || "canonical smoke not set",
      },
    ],
    overviewRows: [
      {
        label: "Tenant",
        value: support.tenant_profile.name,
        status: "ready",
        meta: `${support.tenant_slug} | ${support.entitlements.plan_label}`,
      },
      {
        label: "Queue summary",
        value: `${support.queue_summary.open_total} open`,
        status: support.queue_sla.overdue_total > 0 ? "stale" : "ready",
        meta: `${support.queue_summary.unassigned_total} unassigned | ${support.queue_sla.awaiting_response_total} awaiting response`,
      },
      {
        label: "Compliance retention",
        value: `${support.compliance_settings.reporting_retention_days} days`,
        status: "muted",
        meta: `event logs ${support.compliance_settings.event_log_retention_days} | queue actions ${support.compliance_settings.queue_action_retention_days}`,
      },
      {
        label: "Latest maintenance",
        value: latestMaintenanceStatus(support.maintenance),
        status: latestMaintenanceStatus(support.maintenance) === "completed" ? "ready" : "stale",
        meta: `${(support.maintenance.runs || []).length} tracked runs`,
      },
    ],
    workerRows: workerRuns.length
      ? workerRuns.slice(0, 4).map((item) => ({
          label: item.run_kind,
          value: item.backpressure_active ? "backpressure" : item.latest_status || "unknown",
          status: item.backpressure_active ? "overdue" : item.failure_streak > 0 ? "stale" : "ready",
          meta: `${item.failure_streak} failures | ${item.backpressure_skip_count} skips`,
        }))
      : [
          {
            label: "Worker status",
            value: "no runs",
            status: "muted",
            meta: "No worker health rows were returned for this tenant.",
          },
        ],
    smokeRows: [
      {
        label: "Canonical email",
        value: support.smoke_governance.canonical_email || "not configured",
        status: support.smoke_governance.canonical_email ? "ready" : "stale",
        meta: support.smoke_governance.canonical_role || "unknown role",
      },
      {
        label: "Rotation due",
        value: support.smoke_governance.rotation_due_at || "not scheduled",
        status:
          support.smoke_governance.rotation_status === "ready"
            ? "ready"
            : support.smoke_governance.rotation_status === "unknown"
              ? "stale"
              : "overdue",
        meta: support.smoke_governance.recommended_action,
      },
    ],
    destinationRows,
    selectedDestinationId: selectedDestination ? destinationId(selectedDestination) : null,
    selectedDestination,
    actionRows,
  };
}

export function buildGovernanceDashboardSummary(governance) {
  const actionRows = [];
  if (governance.summary.quarantined_destination_total > 0) {
    actionRows.push({
      kind: "support_destination_recovery",
      label: "Recover quarantined destinations",
      detail: `${governance.summary.quarantined_destination_total} destination(s) need support review and recovery testing.`,
      status: "overdue",
    });
  }
  if (governance.summary.worker_degraded) {
    actionRows.push({
      kind: "review_worker_health",
      label: "Review worker backpressure",
      detail: `${governance.worker_health.dead_letters.length} dead letter(s) currently tracked.`,
      status: "overdue",
    });
  }
  if (governance.summary.smoke_rotation_status !== "ready") {
    actionRows.push({
      kind: "review_smoke_governance",
      label: "Review smoke rotation posture",
      detail: governance.smoke_governance.recommended_action,
      status: "stale",
    });
  }
  if (governance.summary.recent_config_change_total > 0) {
    actionRows.push({
      kind: "review_config_audit",
      label: "Review recent config changes",
      detail: `${governance.summary.recent_config_change_total} recent configuration change(s) should be acknowledged.`,
      status: "stale",
    });
  }
  for (const item of governance.recommended_actions || []) {
    actionRows.push({
      kind: "recommended",
      label: item,
      detail: `${governance.tenant_profile.name} | ${governance.compliance_settings.reporting_retention_days} day reporting retention`,
      status: "ready",
    });
  }
  return {
    postureRows: [
      {
        label: "Overall posture",
        value: governance.summary.overall_status,
        status: governance.summary.overall_status,
        meta: `${governance.summary.recent_incident_total} recent incidents`,
      },
      {
        label: "Smoke rotation",
        value: governance.summary.smoke_rotation_status,
        status: governance.summary.smoke_rotation_status === "ready" ? "ready" : "stale",
        meta: governance.smoke_governance.canonical_email || "canonical smoke not set",
      },
      {
        label: "Worker state",
        value: governance.summary.worker_degraded ? "degraded" : "healthy",
        status: governance.summary.worker_degraded ? "overdue" : "ready",
        meta: `${governance.worker_health.dead_letters.length} dead letters`,
      },
      {
        label: "Destination risk",
        value: governance.summary.quarantined_destination_total,
        status: governance.summary.quarantined_destination_total > 0 ? "stale" : "ready",
        meta: `${governance.summary.long_quarantined_destination_total} long-quarantined`,
      },
    ],
    workerRows: (governance.worker_health.runs || []).length
      ? governance.worker_health.runs.slice(0, 4).map((item) => ({
          label: item.run_kind,
          value: item.backpressure_active ? "backpressure" : item.latest_status || "unknown",
          status: item.backpressure_active ? "overdue" : item.failure_streak > 0 ? "stale" : "ready",
          meta: `${item.failure_streak} failures | ${item.backpressure_skip_count} skips`,
        }))
      : [
          {
            label: "Worker state",
            value: "no runs",
            status: "muted",
            meta: "No worker health rows were returned for this tenant.",
          },
        ],
    riskRows: [
      {
        label: "Config drift",
        value: governance.summary.recent_config_change_total,
        status: governance.summary.recent_config_change_total > 0 ? "stale" : "ready",
        meta: "recent tenant mutations",
      },
      {
        label: "Compliance retention",
        value: `${governance.compliance_settings.reporting_retention_days} days`,
        status: "muted",
        meta: `manager exports ${governance.compliance_settings.manager_exports_enabled ? "enabled" : "disabled"}`,
      },
      {
        label: "Smoke policy",
        value: governance.smoke_governance.rotation_status,
        status:
          governance.smoke_governance.rotation_status === "ready"
            ? "ready"
            : governance.smoke_governance.rotation_status === "unknown"
              ? "stale"
              : "overdue",
        meta: governance.smoke_governance.recommended_action,
      },
    ],
    metricCards: (governance.metrics || []).map((item) => ({
      label: item.label,
      value: item.value,
      detail: item.detail,
      status: item.status,
    })),
    actionRows,
  };
}

export function buildProductUiDiagnosticsSummary(diagnostics) {
  const summary = diagnostics || {
    summary: { total_events: 0, error_events: 0, slow_events: 0, last_event_at: null },
    window_hours: 24,
    slow_event_threshold_ms: 1500,
    event_counts: [],
    surface_counts: [],
    build_counts: [],
    recent_errors: [],
    slowest_events: [],
    recent_events: [],
  };
  const releaseRows = [
    {
      label: "Latest build",
      value: summary.build_counts[0]?.key || "unknown",
      status: summary.build_counts.length ? "ready" : "muted",
      meta: countMetaRows(summary.build_counts),
    },
    {
      label: "Observed surfaces",
      value: summary.surface_counts.length,
      status: summary.surface_counts.length ? "ready" : "muted",
      meta: countMetaRows(summary.surface_counts),
    },
    {
      label: "Event window",
      value: `${summary.window_hours}h`,
      status: "muted",
      meta: `${summary.summary.total_events} total client event(s)`,
    },
  ];
  return {
    metricCards: [
      {
        label: "UI events",
        value: summary.summary.total_events,
        detail: `last event ${summary.summary.last_event_at || "n/a"}`,
        status: summary.summary.total_events > 0 ? "ready" : "muted",
      },
      {
        label: "Client errors",
        value: summary.summary.error_events,
        detail: `${summary.recent_errors.length} recent error samples`,
        status: summary.summary.error_events > 0 ? "overdue" : "ready",
      },
      {
        label: "Slow renders",
        value: summary.summary.slow_events,
        detail: `${summary.slow_event_threshold_ms}ms threshold`,
        status: summary.summary.slow_events > 0 ? "stale" : "ready",
      },
      {
        label: "Top event",
        value: summary.event_counts[0]?.key || "none",
        detail: countMetaRows(summary.event_counts.slice(0, 3)),
        status: summary.event_counts.length ? "ready" : "muted",
      },
    ],
    releaseRows,
    eventRows: (summary.recent_events || []).slice(0, 6).map((item) => ({
      label: item.event_name,
      value: item.outcome,
      status: item.outcome === "success" ? "ready" : "overdue",
      meta: `${item.surface || "global"} | ${item.route || "n/a"} | ${item.duration_ms ?? "n/a"}ms`,
    })),
    errorRows: (summary.recent_errors || []).slice(0, 4).map((item) => ({
      label: item.event_name,
      value: item.detail || item.outcome,
      status: "overdue",
      meta: `${item.surface || "global"} | ${item.route || "n/a"} | ${item.duration_ms ?? "n/a"}ms`,
    })),
    slowRows: (summary.slowest_events || []).slice(0, 4).map((item) => ({
      label: item.event_name,
      value: `${item.duration_ms ?? "n/a"}ms`,
      status: "stale",
      meta: `${item.surface || "global"} | ${item.route || "n/a"} | ${item.client_build || "unknown build"}`,
    })),
  };
}

export function hasEscalatedSupportState(support) {
  return Boolean(
    support.queue_sla.overdue_total > 0 ||
      (support.worker_health.dead_letters || []).length > 0 ||
      (support.notification_destinations || []).some((item) => item.status !== "active")
  );
}

export function hasEscalatedGovernanceState(governance) {
  return Boolean(
    governance.summary.degraded ||
      governance.summary.worker_degraded ||
      governance.summary.quarantined_destination_total > 0
  );
}

export function supportStatusBadge(support) {
  return hasEscalatedSupportState(support) ? "overdue" : "ready";
}

export function governanceStatusBadge(governance) {
  return hasEscalatedGovernanceState(governance) ? "overdue" : "ready";
}
